import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { createPortal } from 'react-dom'
import jp from 'jsonpath'
import classNames from 'classnames'

import TextWidget from '@rjsf/core/lib/components/widgets/TextWidget'
import SchemaField from '@rjsf/core/lib/components/fields/SchemaField'
import RJSFForm, { FormProps, IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'

import { ReactComponent as LeafIcon } from '../../assets/images/external-contact-update-leaf-field-icon.svg'

import { FieldTemplateProps, Widget, WidgetProps, RJSFValidationError, getClosestMatchingOption, FieldProps, RJSFSchema } from '@rjsf/utils'

import {
  DEFAULT_IGNORE_FIELDS,
  getFieldIdParts,
  getJsonPathFromPathParts,
  IGNORE_FIELDS,
  generateUiSchemaToHideEMoneyOwnerType,
  SEPARATOR,
  generateUiSchemaToDisableEMoneyFinancialPrioRank,
  getFieldErrorMessages,
  getFieldSubSectionId,
  getShouldDisplayStatusIcon,
  LABEL_FIELD_KEY_MAP,
  normalizeValue,
  getPreviousValue,
  SEARCH_KEY_MAP,
  DEFAULT_SEARCH_KEY,
} from './utils'

import { useEMoneyOwnerSyncedFormData, useGetSubSectionError } from './hooks'
import { ContactSelectionCheckbox } from './ContactSelectionCheckbox'
import { CustomCheckboxWidget, CustomDateWidget, CustomSelectWidget } from './UpdateExternalContactCustomWidgets'
import { useTracking } from '../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../apps/professional-app/fa/common/tracking'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../common/integrations'
import { ReactComponent as ZocksIcon } from '../../assets/ZocksIcon.svg'

import './UpdateExternalContactForm.scss'
import {
  CustomArrayFieldItemTemplate,
  CustomArrayFieldTemplate,
  CustomBaseInputTemplate,
  CustomObjectFieldTemplate,
} from './UpdateExternalContactCustomTemplates'
import { Button } from '../../components/Button/Button'
import { useAppSelector } from '../../store/hooks'
import { selectCreateNewCRMContact } from '../../store/meetingSummary/contactUpdate'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { JSONSchema7 } from 'json-schema'
import { ExternalContactUpdateResultExplainer } from '../../apps/professional-app/fa/MeetingSummary/ResultExplainer/ExternalContactUpdateResultExplainer'
import { UpdateExternalContactFormProps } from './models'

const getDepth = (id: string, rootId: string) => {
  if (!id.startsWith(rootId)) {
    return 0
  }
  const relativePath = id.slice(rootId.length)
  const parts = relativePath.split(SEPARATOR).filter((p) => p !== '')
  return Math.max(0, parts.length - 1)
}

const getFieldPreviousValue = (formContext: UpdateExternalContactFormProps, fieldId: string): unknown => {
  const { rootFieldId, serviceId, contactValues, previousContactValues, readonly } = formContext
  if (!previousContactValues) {
    return null
  }

  const fieldParts = getFieldIdParts(rootFieldId, fieldId)
  if (readonly) {
    return getPreviousValue(contactValues, previousContactValues, fieldParts, SEARCH_KEY_MAP[serviceId] ?? DEFAULT_SEARCH_KEY)
  }
  return jp.value(previousContactValues, getJsonPathFromPathParts(fieldParts))
}

const CustomOneOfField = (props: FieldProps) => {
  const { schema, onChange, formContext, idSchema, registry, required, formData } = props
  const { schemaUtils } = registry

  const fieldFormContext = registry.formContext as UpdateExternalContactFormProps
  const { rootFieldId, contactValues, hidePreviousValues, serviceId, isContactCreate, onKeySelected, readonly, enableExplainer } = fieldFormContext

  const resolvedSchema = useMemo(() => schemaUtils.retrieveSchema(schema, formData), [schemaUtils, schema, formData])

  const oneOf = useMemo(() => {
    return ((schema.oneOf as JSONSchema7[]) ?? []).map((entry) =>
      schemaUtils.retrieveSchema(
        {
          ...entry,
          $defs: registry.rootSchema?.$defs,
        },
        formData,
      ),
    )
  }, [schema, schemaUtils, formData, registry.rootSchema?.$defs])

  const generatedValue = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, idSchema.$id))
    if (!jsonPath) {
      return contactValues
    }
    return jp.value(contactValues, jsonPath)
  }, [contactValues, idSchema.$id, rootFieldId])

  const previousValue = useMemo(() => getFieldPreviousValue(fieldFormContext, idSchema.$id), [fieldFormContext, idSchema.$id])

  const previousSelectedIndex = useMemo(
    () => getClosestMatchingOption(validator, resolvedSchema, previousValue, oneOf, -1),
    [oneOf, previousValue, resolvedSchema],
  )
  const originalIndex = useMemo(() => getClosestMatchingOption(validator, resolvedSchema, generatedValue, oneOf, -1), [generatedValue, oneOf, resolvedSchema])
  const [selectedIndex, setSelectedIndex] = useState(originalIndex)

  const options = useMemo(
    () =>
      oneOf
        .filter((e) => typeof e === 'object' && e !== null)
        .map((e, index) => ({
          id: `${index}`,
          label: e.title ?? `Option ${index + 1}`,
        })) ?? [],
    [oneOf],
  )

  const selectedSchema = useMemo(() => {
    return selectedIndex > -1 ? oneOf[selectedIndex] : null
  }, [oneOf, selectedIndex])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function extractConstObject(schema: JSONSchema7): any {
    if (schema.properties && typeof schema.properties === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: Record<string, any> = {}
      for (const [key, propSchema] of Object.entries(schema.properties)) {
        if (typeof propSchema === 'object' && 'const' in propSchema) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result[key] = (propSchema as any).const
        }
      }
      return result
    }
    return undefined
  }

  const handleOptionSelected = (option: SelectOption) => {
    if (!option || !option.id) {
      return
    }

    const idx = parseInt(option.id)
    if (idx === selectedIndex) {
      return
    }
    setSelectedIndex(idx)

    const newSchema = oneOf[idx]
    if (!newSchema) {
      return
    }

    const selectedValue =
      'const' in newSchema ? newSchema.const : newSchema.enum && newSchema.enum.length === 1 ? newSchema.enum[0] : extractConstObject(newSchema)

    if (selectedValue === undefined) {
      return
    }

    onChange(selectedValue)
  }

  const showReset = selectedIndex !== originalIndex && originalIndex !== -1

  const handleReset = () => {
    if (originalIndex === -1) {
      return
    }
    setSelectedIndex(originalIndex)
  }

  const fieldParts = getFieldIdParts(rootFieldId, idSchema.$id)
  const fullKeyParts = [...rootFieldId.split(SEPARATOR), ...fieldParts]

  const depth = Math.max(0, getDepth(idSchema.$id, rootFieldId))
  const indentClass = `indent-${depth}`

  const rowClasses = classNames('contact-update-grid data-row', {
    'is-create-mode': isContactCreate,
    'is-readonly-mode': readonly,
  })

  const displayLabel = schema.title ?? 'Option'

  return (
    <div className='d-flex flex-column'>
      <div className={rowClasses}>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          {!readonly && (
            <ContactSelectionCheckbox
              serviceId={serviceId}
              data={formData}
              keyParts={fullKeyParts}
              onKeySelected={onKeySelected}
              className='d-flex contact-information-checkbox'
            />
          )}
          <div className={`d-flex align-items-center ${indentClass}`}>
            <LeafIcon className='hierarchy-connector' />
            <span className='zocks-text-secondary text-content'>{displayLabel}</span>
            {required && !readonly && <span className='zocks-text-danger'>*</span>}
          </div>
        </div>

        {!isContactCreate ? (
          <div className='d-flex align-items-center zocks-text-secondary text-content'>
            {!hidePreviousValues && previousSelectedIndex > -1 ? (
              <span className='text-ellipsis'>{options[previousSelectedIndex].label}</span>
            ) : (
              <span className='fst-italic zocks-text-gray-500'>Not set</span>
            )}
          </div>
        ) : null}

        <div className='d-flex flex-column w-100 ps-2'>
          <div className='d-flex align-items-center w-100 zocks-gap-0.5'>
            {readonly ? (
              <div className='d-flex flex-column w-100 widget-input-wrapper'>
                <span className='zocks-text-gray-900'>
                  {selectedIndex > -1 ? options[selectedIndex].label : <span className='fst-italic zocks-text-gray-500'>Not set</span>}
                </span>
              </div>
            ) : (
              <>
                <div className='d-flex flex-column w-100 widget-input-wrapper'>
                  <SelectDropdown
                    placeholder='Not set'
                    options={options}
                    initialSelectedOption={selectedIndex > -1 ? options[selectedIndex] : null}
                    onOptionSelected={handleOptionSelected}
                    className='w-100'
                  />
                </div>
                {showReset ? (
                  <Button
                    type='button'
                    className='p-0 zocks-text-secondary d-flex align-items-center justify-content-center btn-reset'
                    onClick={handleReset}
                    title='Reset to generated value'
                  >
                    <i className='bi bi-arrow-clockwise' />
                  </Button>
                ) : enableExplainer && originalIndex !== -1 ? (
                  <ExternalContactUpdateResultExplainerWrapper
                    schema={schema}
                    value={options[originalIndex].label}
                    formContext={formContext}
                    fieldId={idSchema.$id}
                    serviceId={serviceId}
                    isContactCreate={isContactCreate}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      {selectedSchema && !readonly ? <SchemaField {...props} schema={selectedSchema} /> : null}
    </div>
  )
}

const transformErrors = (errors: RJSFValidationError[]): RJSFValidationError[] => {
  return errors.map((error) => {
    if (error.name === 'if') {
      return { ...error, message: 'You forgot to fill out some of the required fields.' }
    }
    if (error.name === 'oneOf') {
      return { ...error, message: 'Please choose one of the available options to proceed.' }
    }
    if (error.name === 'required') {
      const prop = error?.params?.missingProperty || error.property?.split('.').pop() || (typeof error.message === 'string' && error.message.split("'")[1])
      if (prop) {
        return { ...error, message: `Please fill out the required '${prop}' field.` }
      }
    }
    if (typeof error.message === 'string' && error.message.toLowerCase().startsWith('must have required property')) {
      const prop = error.message.split("'")[1]
      if (prop) {
        return { ...error, message: `This field can't be empty.` }
      }
    }
    return error
  })
}

export const UpdateExternalContactForm: React.FC<UpdateExternalContactFormProps> = (props) => {
  const {
    serviceId,
    rootFieldId,
    contactSchema,
    contactValues,
    previousContactValues,
    requiredSteps,
    metadata,
    onKeySelected,
    hidePreviousValues = false,
    hideRootFieldHeader = false,
    enableExplainer = false,
    readonly = false,
    isContactCreate: isContactCreateProp,
    title,
    onValidationChange,
  } = props

  const { setValue, watch } = useFormContext()
  const { trackingClient } = useTracking()

  const isContactCreateInStore = useAppSelector(selectCreateNewCRMContact)
  const isContactCreate = isContactCreateProp ?? isContactCreateInStore

  const formRef = useRef<RJSFForm>(null)
  const [formData, setFormData, setInternalFormData] = useEMoneyOwnerSyncedFormData(contactValues, contactSchema, serviceId, rootFieldId, setValue)
  const [editedFields, setEditedFields] = useState<string[]>([])
  const validationTimeoutRef = useRef<NodeJS.Timeout>()

  const externalFormData = watch(rootFieldId)

  useEffect(() => {
    if (externalFormData === undefined) {
      return
    }
    setInternalFormData((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(externalFormData)) {
        return externalFormData
      }
      return prev
    })
  }, [externalFormData, setInternalFormData])

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (onValidationChange && formRef.current) {
      const checkInitialValidation = () => {
        if (formRef.current) {
          const state = formRef.current.state as { errors?: RJSFValidationError[] }
          const errors = state.errors || []
          const hasErrors = errors.length > 0
          onValidationChange(hasErrors, errors.length)
        }
      }
      const timer = setTimeout(checkInitialValidation, 100)
      return () => clearTimeout(timer)
    }
  }, [onValidationChange, formData])

  const handleResetSection = () => {
    setInternalFormData(contactValues)
    setValue(rootFieldId, contactValues)
    setEditedFields([])
  }

  const [areSectionsExpanded, setAreSectionsExpanded] = useState(true)

  const _TextWidget = useMemo(() => wrapWidget(TextWidget), [])
  const _CheckboxWidget = useMemo(() => wrapWidget(CustomCheckboxWidget), [])
  const _SelectWidget = useMemo(() => wrapWidget(CustomSelectWidget), [])
  const _DateWidget = useMemo(() => wrapWidget(CustomDateWidget), [])

  const additionalUiSchema = () => {
    const disableEmoneyFinancialPriosRank = serviceId === IntegrationServiceId.E_MONEY ? generateUiSchemaToDisableEMoneyFinancialPrioRank() : {}
    const hideEmoneyOwner = serviceId === IntegrationServiceId.E_MONEY ? generateUiSchemaToHideEMoneyOwnerType(contactSchema) : {}
    return { ...hideEmoneyOwner, ...disableEmoneyFinancialPriosRank }
  }

  const formContext = useMemo(
    () => ({
      serviceId,
      rootFieldId,
      contactValues,
      previousContactValues,
      requiredSteps,
      metadata,
      onKeySelected,
      hidePreviousValues,
      hideRootFieldHeader,
      areSectionsExpanded,
      isContactCreate,
      readonly,
      enableExplainer,
    }),
    [
      serviceId,
      rootFieldId,
      contactValues,
      previousContactValues,
      requiredSteps,
      metadata,
      onKeySelected,
      hidePreviousValues,
      hideRootFieldHeader,
      areSectionsExpanded,
      isContactCreate,
      readonly,
      enableExplainer,
    ],
  )

  const formProps: FormProps = {
    validator,
    schema: contactSchema,
    formData,
    uiSchema: {
      'ui:rootFieldId': rootFieldId,
      'ui:submitButtonOptions': { norender: true },
      ...additionalUiSchema(),
    },
    templates: {
      FieldTemplate: CustomFieldTemplate,
      ObjectFieldTemplate: CustomObjectFieldTemplate,
      ArrayFieldTemplate: CustomArrayFieldTemplate,
      ArrayFieldItemTemplate: CustomArrayFieldItemTemplate,
      BaseInputTemplate: CustomBaseInputTemplate,
    },
    fields: {
      OneOfField: CustomOneOfField,
    },
    widgets: {
      TextWidget: _TextWidget,
      CheckboxWidget: _CheckboxWidget,
      SelectWidget: _SelectWidget,
      DateWidget: _DateWidget,
    },
    formContext,
    noHtml5Validate: true,
    liveValidate: true,
    showErrorList: false,
    idSeparator: SEPARATOR,
    experimental_defaultFormStateBehavior: {
      emptyObjectFields: 'skipDefaults',
    },
    transformErrors,
  }

  const handleChange = (e: IChangeEvent, id?: string) => {
    setFormData(e.formData)
    if (id !== undefined && !editedFields.includes(id)) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_RESULTS_EDITED,
        eventDetails: { serviceId, fieldId: id },
      })
      setEditedFields((prev) => [...prev, id])
    }

    if (onValidationChange) {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
      validationTimeoutRef.current = setTimeout(() => {
        if (formRef.current) {
          const state = formRef.current.state as { errors?: RJSFValidationError[] }
          const errors = state.errors || []
          const hasErrors = errors.length > 0
          onValidationChange(hasErrors, errors.length)
        }
      }, 300)
    }
  }

  const gridClasses = classNames('contact-update-grid header-row flex-shrink-0', {
    'is-create-mode': isContactCreate,
    'is-readonly-mode': readonly,
  })

  const hasSubSectionErrors = useMemo(() => {
    if (!contactSchema.properties) {
      return false
    }

    return Object.keys(contactSchema.properties).some((key) => {
      const fieldId = `${rootFieldId}${SEPARATOR}${key}`
      const subSection = getFieldSubSectionId(fieldId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const shouldDisplay = getShouldDisplayStatusIcon(subSection, requiredSteps, metadata as Record<string, any>)
      if (!shouldDisplay) {
        return false
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessages = getFieldErrorMessages(requiredSteps, subSection, metadata as Record<string, any>, serviceId)
      return errorMessages.length > 0
    })
  }, [contactSchema.properties, rootFieldId, requiredSteps, metadata, serviceId])

  return (
    <>
      {title && !hideRootFieldHeader && (
        <div className='d-flex align-items-center justify-content-between zocks-p-1.0 pb-0 zocks-mb-1'>
          <h4 className='m-0 fw-bold'>{title}</h4>
          {!readonly && (
            <Button variant='link' className='p-0 text-decoration-none zocks-text-secondary d-flex align-items-center gap-1' onClick={handleResetSection}>
              <i className='bi bi-arrow-counterclockwise' />
              <span className='fw-bold'>Reset {title}</span>
            </Button>
          )}
        </div>
      )}
      {hasSubSectionErrors && (
        <div className='d-flex align-self-center gap-3 align-items-center zocks-bg-color-warning gap-3 justify-content-start rounded p-2 mb-3'>
          <i className='bi bi-exclamation-triangle text-warning consent-settings-info-square' />
          <span className='zocks-text-gray-900'>
            Updates synced. Some need your review due to {getIntegrationDisplayName(serviceId)} requirements. Check and apply them manually.
          </span>
        </div>
      )}

      <div className='contact-update-container zocks-bg-color-white zocks-border-0.25 zocks-border-gray-300 border d-flex flex-column h-100'>
        <div className='flex-grow-1 overflow-auto'>
          {!hideRootFieldHeader && (
            <div className={gridClasses}>
              <div className='d-flex align-items-center zocks-gap-0.5'>
                {!readonly && (
                  <ContactSelectionCheckbox
                    serviceId={serviceId}
                    data={formData}
                    keyParts={rootFieldId.split(SEPARATOR)}
                    onKeySelected={onKeySelected}
                    className='d-flex contact-information-checkbox'
                  />
                )}
                <div className='d-flex align-items-center cursor-pointer user-select-none' onClick={() => setAreSectionsExpanded(!areSectionsExpanded)}>
                  <span className='zocks-text-gray-700 fw-bold small'>CATEGORY</span>
                  <i className={`bi bi-arrows-collapse ms-1 zocks-text-secondary icon-tiny`} />
                </div>
              </div>

              {!isContactCreate ? (
                <div className='d-flex align-items-center zocks-gap-0.25 zocks-p-0.5'>
                  <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
                  <span className='zocks-text-gray-700 fw-bold small'>{readonly ? 'PREVIOUS DATA' : 'CURRENT DATA'}</span>
                  <Tooltip
                    placement='top'
                    title={
                      readonly
                        ? `Data in this column reflects what was stored in your ${getIntegrationDisplayName(serviceId)} when Zocks captured this update.`
                        : `Data in this column reflects what is currently stored in your ${getIntegrationDisplayName(serviceId)}. 'Not set' indicates this field is currently empty in ${getIntegrationDisplayName(serviceId)}.`
                    }
                  >
                    <i className='bi bi-info-square zocks-text-gray-700 ms-1' />
                  </Tooltip>
                </div>
              ) : null}

              <div className='d-flex align-items-center zocks-gap-0.25 zocks-p-0.5'>
                {readonly ? (
                  <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
                ) : (
                  <ZocksIcon width='1rem' height='1rem' fill='black' />
                )}

                <span className='zocks-text-gray-700 fw-bold small'>{readonly ? 'SAVED DATA' : 'CAPTURED DATA'}</span>
                {!readonly && (
                  <Tooltip
                    placement='top'
                    title={`Data in this column reflects what Zocks identified and will be pushed to ${getIntegrationDisplayName(serviceId)} when you click the Update button.`}
                  >
                    <i className='bi bi-info-square zocks-text-gray-700 ms-1' />
                  </Tooltip>
                )}
              </div>
            </div>
          )}
          <RJSFForm {...formProps} ref={formRef} onChange={handleChange} readonly={readonly} />
        </div>
      </div>
    </>
  )
}

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const { id: fieldId, schema, children, registry, required } = props
  const { rootFieldId, serviceId, contactValues, readonly } = registry.formContext as UpdateExternalContactFormProps

  const isRoot = fieldId === rootFieldId

  const isObject = schema.type === 'object'
  const isArray = schema.type === 'array'
  const isConstant = schema.const !== undefined

  const generatedValue = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, fieldId))
    if (!jsonPath) {
      return contactValues
    }
    return jp.value(contactValues, jsonPath) ?? null
  }, [contactValues, fieldId, rootFieldId])

  const isEmpty = generatedValue === undefined || (isObject && !Object.keys(generatedValue || {}).length) || (isArray && !(generatedValue || []).length)

  const ignoreFields = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS
  const fieldIdParts = fieldId.split(SEPARATOR)
  const shouldIgnore = ignoreFields.includes(fieldIdParts[fieldIdParts.length - 1])

  if (shouldIgnore || (!required && isEmpty) || isConstant) {
    return null
  }

  if (isRoot) {
    return <>{children}</>
  }

  if ((isObject || isArray) && !isRoot) {
    if (readonly && isObject && schema.oneOf) {
      return <>{children}</>
    }
    return <CollapsibleObjectRow {...props} />
  }

  return <>{children}</>
}

const ErrorMessageComponent: React.FC<{ errorMessages: string[] }> = ({ errorMessages }) => {
  return (
    <div className='p-3 zocks-text-white text-start'>
      <div className='fw-bold mb-2'>The following errors occurred during operation execution.</div>
      <ul className='mb-2 ps-3'>
        {errorMessages.map((msg, index) => (
          <li key={index}>{msg}</li>
        ))}
      </ul>
      <div className='small'>Check your Integration's field names or contact your admin.</div>
    </div>
  )
}

const CollapsibleObjectRow: React.FC<FieldTemplateProps> = (props) => {
  const { id: fieldId, schema, children, registry, formData } = props
  const {
    serviceId,
    onKeySelected,
    previousContactValues,
    rootFieldId,
    areSectionsExpanded,
    isContactCreate,
    requiredSteps,
    metadata,
    hideRootFieldHeader,
    readonly,
  } = registry.formContext as UpdateExternalContactFormProps

  const [isOpen, setIsOpen] = useState(areSectionsExpanded ?? true)
  const [prevGlobalExpanded, setPrevGlobalExpanded] = useState(areSectionsExpanded)

  if (areSectionsExpanded !== prevGlobalExpanded) {
    setIsOpen(areSectionsExpanded ?? true)
    setPrevGlobalExpanded(areSectionsExpanded)
  }

  const fieldParts = getFieldIdParts(rootFieldId, fieldId)
  const fullKeyParts = [...rootFieldId.split(SEPARATOR), ...fieldParts]

  const isNew = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, fieldId))
    const prevVal = previousContactValues ? jp.value(previousContactValues, jsonPath) : undefined
    return !prevVal && formData
  }, [previousContactValues, fieldId, rootFieldId, formData])

  const depth = Math.max(0, getDepth(fieldId, rootFieldId))
  const indentClass = `indent-${depth}`
  const isRootHeader = depth === 0
  const isSecondLevelHeader = depth === 1

  const { errorMessages } = useGetSubSectionError({
    fieldId,
    requiredSteps,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata: metadata as { [key: string]: any },
    serviceId,
  })

  const rowClasses = classNames('contact-update-grid data-row accordion-toggle', {
    'is-create-mode': isContactCreate,
    'is-readonly-mode': readonly,
    'position-sticky zocks-bg-color-white z-1': isRootHeader || isSecondLevelHeader,
    'sticky-header-no-offset': isRootHeader && hideRootFieldHeader,
    'sticky-header-offset': isRootHeader && !hideRootFieldHeader,
    'sticky-sub-header-no-offset': isSecondLevelHeader && hideRootFieldHeader,
    'sticky-sub-header-offset': isSecondLevelHeader && !hideRootFieldHeader,
  })

  if (isRootHeader && metadata && Object.keys(metadata).length > 0 && errorMessages.length === 0) {
    return null
  }

  const objectLabel = LABEL_FIELD_KEY_MAP[serviceId] && formData?.[LABEL_FIELD_KEY_MAP[serviceId]] ? formData[`${LABEL_FIELD_KEY_MAP[serviceId]}`] : 'Item'

  return (
    <div className='border-bottom'>
      <div className={rowClasses} onClick={() => setIsOpen(!isOpen)}>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <div onClick={(e) => e.stopPropagation()}>
            {!readonly && (
              <ContactSelectionCheckbox
                serviceId={serviceId}
                data={formData}
                keyParts={fullKeyParts}
                onKeySelected={onKeySelected}
                className='d-flex contact-information-checkbox'
              />
            )}
          </div>
          <div className={`d-flex align-items-center ${indentClass}`}>
            {!isRootHeader && <LeafIcon className='hierarchy-connector' />}
            <span className='fw-bold'>{schema.title || objectLabel}</span>
            {isNew && !readonly && <span className='badge zocks-bg-color-primary badge-new zocks-ms-0.5'>NEW</span>}
            <i className={`bi ${isOpen ? 'bi-caret-down-fill' : 'bi-caret-right-fill'} ms-2 zocks-text-secondary`} />
            {!readonly && errorMessages.length > 0 && isRootHeader && (
              <Tooltip placement='top' tooltipContent={<ErrorMessageComponent errorMessages={errorMessages} />}>
                <i className='bi bi-exclamation-square text-danger ms-2' />
              </Tooltip>
            )}
          </div>
        </div>
        {!isContactCreate ? <div /> : null}
        <div />
      </div>
      {isOpen && <div className='zocks-bg-color-white'>{children}</div>}
    </div>
  )
}

const wrapWidget = (OriginalWidget: Widget) => {
  return (props: WidgetProps) => {
    const { id: fieldId, label, schema, value, required, registry, title, onChange, rawErrors } = props

    const formContext = registry.formContext as UpdateExternalContactFormProps

    const { rootFieldId, serviceId, contactValues, onKeySelected, isContactCreate, readonly, enableExplainer } = formContext

    const [isEditing, setIsEditing] = useState(false)

    const previousValue = useMemo(() => getFieldPreviousValue(formContext, fieldId), [formContext, fieldId])

    const originalValue = useMemo(() => {
      const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, fieldId))
      return contactValues ? jp.value(contactValues, jsonPath) : null
    }, [contactValues, fieldId, rootFieldId])

    const fieldParts = getFieldIdParts(rootFieldId, fieldId)
    const fullKeyParts = [...rootFieldId.split(SEPARATOR), ...fieldParts]

    const displayLabel = title ?? label

    const isValueEmpty = value === undefined || value === null || value === ''
    const isOriginalEmpty = originalValue === undefined || originalValue === null || originalValue === ''
    const isPreviousEmpty = previousValue === undefined || previousValue === null || previousValue === ''

    const validationErrors = rawErrors || []
    const hasError = validationErrors.length > 0
    const errorMessage = validationErrors[0]

    if (readonly) {
      if (isValueEmpty) {
        return null
      }
    } else {
      if (isOriginalEmpty && isValueEmpty && !isEditing && !hasError && !required) {
        return null
      }
    }

    const showAddButton = !isPreviousEmpty && !isValueEmpty && previousValue === value && !isEditing && !hasError

    const handleReset = () => {
      onChange(originalValue)
      setIsEditing(false)
    }

    const showReset = (JSON.stringify(value) !== JSON.stringify(originalValue) || isEditing) && !showAddButton
    const wrapperClass = hasError ? 'is-invalid-wrapper' : ''
    const depth = Math.max(0, getDepth(fieldId, rootFieldId))
    const indentClass = `indent-${depth}`

    const rowClasses = classNames('contact-update-grid data-row', {
      'is-create-mode': isContactCreate,
      'is-readonly-mode': readonly,
    })

    const handleOnChange = (newValue: string | number | boolean | null | undefined) => {
      setIsEditing(true)
      if (newValue === '' || newValue === null) {
        onChange(undefined)
      } else {
        onChange(newValue)
      }
    }

    return (
      <div className={rowClasses}>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          {!readonly && (
            <ContactSelectionCheckbox
              serviceId={serviceId}
              data={value}
              keyParts={fullKeyParts}
              onKeySelected={onKeySelected}
              className='d-flex contact-information-checkbox'
            />
          )}
          <div className={`d-flex align-items-center ${indentClass}`}>
            <LeafIcon className='hierarchy-connector' />
            <span className='zocks-text-secondary text-content'>{displayLabel}</span>
            {required && !readonly && <span className='zocks-text-danger'>*</span>}
          </div>
        </div>

        {!isContactCreate ? (
          <div className='d-flex align-items-center zocks-text-secondary text-content'>
            {!isPreviousEmpty ? <span>{normalizeValue(previousValue, schema)}</span> : <span className='fst-italic zocks-text-gray-500'>Not set</span>}
          </div>
        ) : null}

        <div className='d-flex align-items-center w-100 ps-2'>
          <div className='d-flex flex-column w-100'>
            {readonly ? (
              <div className='zocks-text-gray-900'>
                {!isValueEmpty ? (
                  typeof value === 'boolean' ? (
                    value ? (
                      'Yes'
                    ) : (
                      'No'
                    )
                  ) : (
                    normalizeValue(value, schema)
                  )
                ) : (
                  <span className='fst-italic zocks-text-gray-500'>Not set</span>
                )}
              </div>
            ) : (
              <>
                <div className='d-flex align-items-center w-100 zocks-gap-0.5'>
                  <div className={`widget-input-wrapper ${wrapperClass}`}>
                    <OriginalWidget {...props} onChange={handleOnChange} autofocus={isEditing} />
                  </div>
                  {showReset ? (
                    <Button
                      type='button'
                      className='p-0 zocks-text-secondary d-flex align-items-center justify-content-center btn-reset'
                      onClick={handleReset}
                      title='Reset to generated value'
                    >
                      <i className='bi bi-arrow-clockwise' />
                    </Button>
                  ) : enableExplainer && !isOriginalEmpty ? (
                    <ExternalContactUpdateResultExplainerWrapper
                      schema={schema}
                      value={normalizeValue(value, schema)}
                      formContext={formContext}
                      fieldId={fieldId}
                      serviceId={serviceId}
                      isContactCreate={isContactCreate}
                    />
                  ) : null}
                </div>
                {hasError && <div className='zocks-text-danger zocks-mt-1 text-error'>{errorMessage}</div>}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
}

interface PortalPositionProps {
  top: number
  right: number
}

interface ExternalContactUpdateResultExplainerWrapperProps {
  schema: RJSFSchema
  value: string
  formContext: UpdateExternalContactFormProps
  fieldId: string
  serviceId: IntegrationServiceId
  isContactCreate: boolean | undefined
}

const ExternalContactUpdateResultExplainerWrapper: React.FC<ExternalContactUpdateResultExplainerWrapperProps> = ({
  schema,
  value,
  formContext,
  fieldId,
  serviceId,
  isContactCreate,
}) => {
  const { tracked } = useTracking()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<PortalPositionProps>({
    top: 0,
    right: 0,
  })

  const _handleQuoteButtonClick = () => {
    setIsOpen(true)
  }

  const handleQuoteButtonClick = tracked(_handleQuoteButtonClick, {
    eventName: TrackEvent.CONTACT_UPDATE_RESULTS_EXPLAINED,
    eventDetails: {
      serviceId,
      method: isContactCreate ? 'create' : 'update',
    },
  })

  const handleClose = () => {
    setIsOpen(false)
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return
      }

      const buttonRect = buttonRef.current.getBoundingClientRect()
      const modalRect = document.body.getBoundingClientRect()
      setPosition({
        top: buttonRect.top - modalRect.top - 9,
        right: modalRect.right - buttonRect.right,
      })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  return (
    <div className='position-relative'>
      <Button ref={buttonRef} type='button' className='fs-6 bi-quote quote-button p-1' onClick={handleQuoteButtonClick} />
      {isOpen
        ? createPortal(
            <div
              className='field-explainer-popover-border rounded-4 zocks-bg-color-white position-absolute p-2 overflow-y-auto'
              style={{ top: position.top, right: position.right }}
            >
              <ExternalContactUpdateResultExplainer schema={schema} value={value} formContext={formContext} fieldId={fieldId} onClose={handleClose} />
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
